import { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Loader2, X, Search } from 'lucide-react';
import api from '../../lib/api';
import { useIsSupplier } from '../../hooks/usePortalMode';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { DocumentVisibility } from './types';

// CR-5325: Three-state visibility picker (private / shared / public) +
// per-share company picker. Extracted from UploadDialog so the Edit
// dialog can reuse the exact same UX without duplication. Both sides
// share connection-fetch caching, audience-aware copy, and the share
// search UI.

interface ConnectionCompany {
  id: number;
  name: string;
  company_type: string;
}

interface VisibilityFieldProps {
  visibility: DocumentVisibility;
  onVisibilityChange: (next: DocumentVisibility) => void;
  sharedCompanyIds: number[];
  onSharedCompanyIdsChange: (next: number[]) => void;
}

export function VisibilityField({
  visibility,
  onVisibilityChange,
  sharedCompanyIds,
  onSharedCompanyIdsChange,
}: VisibilityFieldProps) {
  const isSupplier = useIsSupplier();
  const { isSupplierPortalMode } = useMenuSettings();
  const [connections, setConnections] = useState<ConnectionCompany[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [search, setSearch] = useState('');

  // In standard Helix a supplier shares documents with their 'customers'. In
  // supplier_portal mode the platform supplier is a customer whose counterparties
  // are displayed as 'suppliers' throughout the UI, so the audience noun flips.
  const baseNoun = isSupplier ? 'customer' : 'supplier';
  const audienceNoun = isSupplierPortalMode
    ? (baseNoun === 'customer' ? 'supplier' : 'customer')
    : baseNoun;
  const audiencePlural = `${audienceNoun}s`;

  useEffect(() => {
    if (visibility !== 'shared' || connections.length > 0) return;
    setLoadingConnections(true);
    api.get<ConnectionCompany[]>('/core/companies/my_connections/', { params: { limit: 2000 } })
      .then(res => setConnections(Array.isArray(res.data) ? res.data : []))
      .catch(() => setConnections([]))
      .finally(() => setLoadingConnections(false));
  }, [visibility, connections.length]);

  const toggleCompany = (id: number) => {
    const next = sharedCompanyIds.includes(id)
      ? sharedCompanyIds.filter(x => x !== id)
      : [...sharedCompanyIds, id];
    onSharedCompanyIdsChange(next);
  };

  const setVisibility = (v: DocumentVisibility) => {
    if (v !== 'shared') {
      onSharedCompanyIdsChange([]);
    }
    onVisibilityChange(v);
  };

  const filteredConnections = connections.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase())
  );
  const selectedIds = new Set(sharedCompanyIds);
  const selectedConnections = connections.filter(c => selectedIds.has(c.id));

  return (
    <div className="space-y-3">
      <div>
        <Label>Visibility</Label>
        <p className="text-xs text-muted-foreground mt-0.5">
          Who can see this document.
        </p>
      </div>
      <div className={`grid ${isSupplier ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
        <VisibilityChoice
          active={visibility === 'private'}
          title="Private"
          description={isSupplier
            ? 'Only your organisation can see this document.'
            : 'Only users at your company can see this document.'}
          onClick={() => setVisibility('private')}
        />
        <VisibilityChoice
          active={visibility === 'shared'}
          title="Shared"
          description={`Pick specific ${audiencePlural} to give visibility.`}
          onClick={() => setVisibility('shared')}
        />
        {/* Public is supplier-only — customer uploads are force-marked
            is_public=false on the backend, so offering this to a customer
            would silently ignore their click. */}
        {isSupplier && (
          <VisibilityChoice
            active={visibility === 'public'}
            title="Public"
            description={`Visible to every ${audienceNoun} on the platform.`}
            onClick={() => setVisibility('public')}
          />
        )}
      </div>

      {visibility === 'shared' && (
        <div className="space-y-2 border rounded-md p-3 bg-muted/20">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">
              Select {audiencePlural}
              {selectedConnections.length > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {selectedConnections.length} selected
                </span>
              )}
            </p>
          </div>
          {selectedConnections.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedConnections.map(c => (
                <Badge key={c.id} variant="secondary" className="gap-1">
                  {c.name}
                  <button
                    type="button"
                    onClick={() => toggleCompany(c.id)}
                    className="hover:text-foreground text-muted-foreground"
                    aria-label={`Remove ${c.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder={`Search ${audiencePlural}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {!loadingConnections && connections.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing {filteredConnections.length} of {connections.length} {audiencePlural}
              {!search && connections.length > 50 ? ' — type to filter' : ''}
            </p>
          )}
          <div className="max-h-64 overflow-y-auto border rounded-md bg-background">
            {loadingConnections && (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading {audiencePlural}…
              </div>
            )}
            {!loadingConnections && filteredConnections.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">
                {search ? `No ${audiencePlural} match your search.` : `No ${audiencePlural} available.`}
              </p>
            )}
            {!loadingConnections && filteredConnections.map(c => {
              const active = selectedIds.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCompany(c.id)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2 hover:bg-muted/60 transition-colors ${active ? 'bg-primary/5' : ''}`}
                >
                  <span className="truncate">{c.name}</span>
                  {active && <span className="text-xs text-primary font-medium">✓ Selected</span>}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground pt-1">
            Only the {audienceNoun}{selectedConnections.length !== 1 ? 's' : ''} selected above will see this document.
          </p>
        </div>
      )}
    </div>
  );
}

interface VisibilityChoiceProps {
  active: boolean;
  title: string;
  description: string;
  onClick: () => void;
}

function VisibilityChoice({ active, title, description, onClick }: VisibilityChoiceProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-md border p-3 transition-colors ${active ? 'border-primary bg-primary/5' : 'hover:border-foreground/30'}`}
    >
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{description}</p>
    </button>
  );
}
